// apps/provider/src/services/api/orders.js
// Physician orders CRUD — order catalog items and provider orders
// (imaging, referrals, DME, procedures, therapy, other).
// Separate from lab orders which use the Labcorp extension.
//
// Methods: getOrderCatalogItems, createOrderCatalogItem, updateOrderCatalogItem,
//          deleteOrderCatalogItem, getProviderOrders, getProviderOrder,
//          createProviderOrder, updateProviderOrder, deleteProviderOrder
//
// Response types: @prism-ehr/shared → OrderCatalogItem, ProviderOrder, ProviderOrderWithAddons

/**
 * @returns {Object} Orders domain methods (bound to `this` of the composed apiService)
 */
export function createOrdersMethods() {
  return {

    // ============ Order Catalog Items ============

    /**
     * Get order catalog items with optional filters.
     *
     * Xano: GET /order-catalog-item
     * Xano staging file: order-catalog-item_GET.xs
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.organization_id]
     * @param {string} [filters.type]
     * @param {boolean} [filters.is_active]
     * @returns {Promise<import('@prism-ehr/shared').OrderCatalogItem[]>}
     */
    getOrderCatalogItems(filters = {}) {
      const queryParams = new URLSearchParams();

      if (filters.organization_id) queryParams.append('organization_id', filters.organization_id);
      if (filters.type) queryParams.append('type', filters.type);
      if (filters.is_active !== undefined) queryParams.append('is_active', filters.is_active);

      return this.request(`/order-catalog-item?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Create a new order catalog item.
     *
     * Xano: POST /order-catalog-item
     * Xano staging file: order-catalog-item_POST.xs
     *
     * @param {Object} data
     * @returns {Promise<import('@prism-ehr/shared').OrderCatalogItem>}
     */
    createOrderCatalogItem(data) {
      return this.request('/order-catalog-item', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Update an order catalog item.
     *
     * Xano: PATCH /order-catalog-item/{item_id}
     * Xano staging file: order-catalog-item_{item_id}_PATCH.xs
     *
     * @param {string} itemId
     * @param {Object} updates
     * @returns {Promise<import('@prism-ehr/shared').OrderCatalogItem>}
     */
    updateOrderCatalogItem(itemId, updates) {
      return this.request(`/order-catalog-item/${itemId}`, {
        method: 'PATCH',
        body: JSON.stringify(updates)
      });
    },

    /**
     * Delete an order catalog item.
     *
     * Xano: DELETE /order-catalog-item/{item_id}
     * Xano staging file: order-catalog-item_{item_id}_DELETE.xs
     *
     * @param {string} itemId
     * @returns {Promise<void>}
     */
    deleteOrderCatalogItem(itemId) {
      return this.request(`/order-catalog-item/${itemId}`, {
        method: 'DELETE'
      });
    },

    // ============ Provider Orders ============

    /**
     * Get provider orders with optional filters.
     * Includes patient addon for dashboard display.
     *
     * Xano: GET /provider-order
     * Xano staging file: provider-order_GET.xs
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.organization_id]
     * @param {string} [filters.patient_id]
     * @param {string} [filters.status]
     * @param {string} [filters.order_type]
     * @returns {Promise<import('@prism-ehr/shared').ProviderOrderWithAddons[]>}
     */
    getProviderOrders(filters = {}) {
      const queryParams = new URLSearchParams();

      if (filters.organization_id) queryParams.append('organization_id', filters.organization_id);
      if (filters.patient_id) queryParams.append('patient_id', filters.patient_id);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.order_type) queryParams.append('order_type', filters.order_type);

      return this.request(`/provider-order?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Get a single provider order with addons (patient, catalog_item, ordering_provider).
     *
     * Xano: GET /provider-order/{order_id}
     * Xano staging file: provider-order_{order_id}_GET.xs
     *
     * @param {string} orderId
     * @returns {Promise<import('@prism-ehr/shared').ProviderOrderWithAddons>}
     */
    getProviderOrder(orderId) {
      return this.request(`/provider-order/${orderId}`, {
        method: 'GET'
      });
    },

    /**
     * Create a new provider order.
     *
     * Xano: POST /provider-order
     * Xano staging file: provider-order_POST.xs
     *
     * @param {Object} data
     * @returns {Promise<import('@prism-ehr/shared').ProviderOrder>}
     */
    createProviderOrder(data) {
      return this.request('/provider-order', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Update a provider order (including result entry).
     *
     * Xano: PATCH /provider-order/{order_id}
     * Xano staging file: provider-order_{order_id}_PATCH.xs
     *
     * @param {string} orderId
     * @param {Object} updates
     * @returns {Promise<import('@prism-ehr/shared').ProviderOrder>}
     */
    updateProviderOrder(orderId, updates) {
      return this.request(`/provider-order/${orderId}`, {
        method: 'PATCH',
        body: JSON.stringify(updates)
      });
    },

    /**
     * Delete a provider order (drafts only).
     *
     * Xano: DELETE /provider-order/{order_id}
     * Xano staging file: provider-order_{order_id}_DELETE.xs
     *
     * @param {string} orderId
     * @returns {Promise<void>}
     */
    deleteProviderOrder(orderId) {
      return this.request(`/provider-order/${orderId}`, {
        method: 'DELETE'
      });
    },
  };
}
